import { useCallback, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router";
import { processError } from "../../../../common/utils/base_error";

import { downloadBase64File } from "../../../../common/utils/download_utils";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useCachedDataLoader } from "../../../../common/hooks/use_cached_data_loader";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";

import CommonConstants from "../../../../common/constants";
import { useForm } from "react-hook-form";
import {
  FuncionarioTercForm,
  FuncionarioTercSchema,
} from "../schemas/Funcionario_Terc/funcionario_ter";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFuncionariosTercerizacaoFilters } from "../hooks/Funcionarios_tercerizacao_lista/filters";
import { useFuncionariosTercerizacaoPagination } from "../hooks/Funcionarios_tercerizacao_lista/pagination";
import {
  EnderecoForm,
  EnderecoSchema,
} from "../schemas/Funcionario_Terc/endereco";
import { uploadFileToBase64String } from "../../../../common/utils/utils";
import { FuncionarioTercLista } from "../../domain/models/funcionarios_tercerizado_lista";
import { CentroCustoSecundario } from "../../../rh/domain/models/centro_custo_secundario";
import { CentroCustoSecundarioForm } from "../../../rh/presentation/schemas/Funcionarios_cadastro/centro_custo_secundario";
import { Documento } from "../../../rh/domain/models/documento";
import { DocumentoForm } from "../../../rh/presentation/schemas/Funcionarios_cadastro/documento_schema";
import { FuncionarioTreinamento } from "../../../rh/domain/models/funcionario_treinamento";
import { TreinamentoForm } from "../../../rh/presentation/schemas/Funcionarios_cadastro/treinamento_schema";
import { AvaliarTreinamentoForm } from "../../../rh/presentation/schemas/Funcionarios_cadastro/avaliar_treinamento_schema";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { useDocumentoFilters } from "../hooks/documentos_lista/filters";
import {
  useCreateDebounceHandler,
  useDebouncedCallback,
} from "../../../../common/hooks/use_debounced_callback";

type LoadFuncionarioTercState = {
  IdFuncionarioTerc?: number;
};
type LoadDocumentosState = {
  id?: number;
  status?: number;
  situacao?: number;
  DataDe?: Date | null;
  DataAte?: Date | null;
  NomeDocumento?: string | null;
  page?: number;
  rowsPerPage?: number;
};
export const useFuncionariosCadastroViewModel = () => {
  const {
    commons: {
      getCentroCustoDropDownUseCase,
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    },
    reports: { getReportFuncionarioTreinamentoTercUseCase },
    terceirizacao: {
      createFuncionariosTerUseCase,
      updateFuncionariosTercUseCase,
      createCentroCustoSecundarioUseCase,
      updateCentroCustoSecundarioUseCase,
      getCentroCustoSecundariosUseCase,
      deleteCentroCustoSecundarioUseCase,
      getAllListagemFuncionariosTercUseCase,
      createDocumentoUseCase,
      updateDocumentoUseCase,
      deleteDocumentoUseCase,
      getDocumentosUseCase,
      getFuncionarioTreinamentosUseCase,
      createFuncionarioTreinamentoUseCase,
      updateFuncionarioTreinamentoUseCase,
      deleteFuncionarioTreinamentoUseCase,
      avaliarTreinamentoUseCase,
      downloadFuncionarioDocumentoByIdUseCase,
    },
    rh: { getCepUseCase, getTreinamentosDropDownUseCase },
  } = useDependencyInjection();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });
  const backOrNavigate = useBackOrNavigate();
  const {
    setCustomLoading,
    setPageSaving,
    pushErrorNotification,
    pushInfoNotification,
  } = usePageLoader();
  const { id } = useParams();
  const debouncedRef = useCreateDebounceHandler(750);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [currentTab, setCurrentTab] = useState(1);
  const [idFuncionario, setIdFuncionario] = useState<number | undefined>(
    undefined,
  );
  const [funcionario, setFuncionario] = useState<FuncionarioTercLista>();
  const [fileUpload, setFileUpload] = useState<File | null>(null);
  const [fileUploadUrl, setFileUploadURL] = useState<string | null>(null);

  //#region Relatorio
  const [reportViewerState, startReportViewerProcess] = useReportViewer();
  //#endregion

  const hookForm = useForm<FuncionarioTercForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    disabled: loading,
    resolver: zodResolver(FuncionarioTercSchema),
    defaultValues: {
      Ativo: 1,
      StatusAso: 2,
    },
  });
  const hookFormEndereco = useForm<EnderecoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    disabled: loading,
    resolver: zodResolver(EnderecoSchema),
  });

  const filters = useFuncionariosTercerizacaoFilters();
  const pagination = useFuncionariosTercerizacaoPagination();

  const filtersDoc = useDocumentoFilters();

  //#region Centro Custo
  const [centroCustoSecundarios, setCentroCustoSecundarios] =
    useState<CentroCustoSecundario[]>();
  const [selectedCentroCustoSecundario, setSelectedCentroCustoSecundario] =
    useState<CentroCustoSecundario>();
  const [
    openCadastroCentroCustoSecundarioModal,
    setOpenCadastroCentroCustoSecundarioModal,
  ] = useState<boolean>(false);
  const [
    openDeletarCentroCustoSecundarioModal,
    setOpenDeletarCentroCustoSecundarioModal,
  ] = useState(false);
  //#endregion

  //#region
  const [selectedDocumento, setSelectedDocumento] = useState<Documento>();
  const [openCadastroDocumentoModal, setOpenCadastroDocumentoModal] =
    useState<boolean>(false);
  const [openDocumentoModal, setOpenDocumentoModal] = useState<boolean>(false);
  const [openDeletarDocumentoModal, setOpenDeletarDocumentoModal] =
    useState(false);
  const [documentos, setDocumentos] = useState<Documento[]>();
  //#endregion

  //#region  Treinamento
  const [funcionarioTreinamentos, setFuncionarioTreinamentos] =
    useState<FuncionarioTreinamento[]>();
  const [selectedFuncionarioTreinamento, setSelectedFuncionarioTreinamento] =
    useState<FuncionarioTreinamento>();
  const [
    openCadastroFuncionarioTreinamentoModal,
    setOpenCadastroFuncionarioTreinamentoModal,
  ] = useState(false);
  const [
    selectedIdFuncionarioTreinamentosLote,
    setSelectedIdFuncionarioTreinamentosLote,
  ] = useState<number[]>();
  const [
    openAvaliarFuncionarioTreinamentoModal,
    setOpenAvaliarFuncionarioTreinamentoModal,
  ] = useState(false);
  const [
    openDeletarFuncionarioTreinamentoModal,
    setOpenDeletarFuncionarioTreinamentoModal,
  ] = useState(false);
  const [
    openDeletarFuncionarioTreinamentosLoteModal,
    setOpenDeletarFuncionarioTreinamentosLoteModal,
  ] = useState(false);
  //#endregion

  const isCreated = useMemo(() => {
    return idFuncionario !== undefined && idFuncionario !== null;
  }, [idFuncionario]);

  //#region load
  const loadCentroCustoDropDown = useCachedDataLoader({
    key: CommonConstants.cacheKey.centroCustoDropDown,
    loader: async () =>
      await getCentroCustoDropDownUseCase.getCentroCustoDropDown(),
    onSet: (dt) =>
      useFuncionariosTercerizacaoFilters.getState().setCentroDeCustoLista(dt),
    onError: (e) => {
      useFuncionariosTercerizacaoFilters.getState().setCentroDeCustoLista([]);
      throw e;
    },
  });
  const loadTreinamentosDropDown = useCachedDataLoader({
    key: CommonConstants.cacheKey.treinamentosDropDown,
    loader: async () =>
      await getTreinamentosDropDownUseCase.getTreinamentosDropDown(),
    onSet: (dt) =>
      useFuncionariosTercerizacaoFilters.getState().setTreinamentoLista(dt),
    onError: (e) => {
      useFuncionariosTercerizacaoFilters.getState().setTreinamentoLista([]);
      throw e;
    },
  });
  const loadFuncionarioTreinamentos = useCallback(
    async (params?: { id?: number; page?: number; rowsPerPage?: number }) => {
      try {
        const id = params?.id ?? idFuncionario;

        if (id) {
          await getFuncionarioTreinamentosUseCase.getFuncionarioTreinamentos(
            id,
            1,
            200,
          );
          setFuncionarioTreinamentos(getFuncionarioTreinamentosUseCase.items);
        }
      } catch (e: unknown) {
        setFuncionarioTreinamentos([]);
        throw e;
      }
    },
    [getFuncionarioTreinamentosUseCase, idFuncionario],
  );

  const loadFuncionarioTerc = useCallback(
    async (funcState?: LoadFuncionarioTercState) => {
      try {
        const filteredIdFuncionarioTerc =
          funcState?.IdFuncionarioTerc ?? filters.IdFuncionarioTerc;
        await getAllListagemFuncionariosTercUseCase.getAllListagemFuncionarioTerc(
          { IdFuncionarioTerc: filteredIdFuncionarioTerc },
          1,
          1,
        );
        const funcioanrioTerc =
          getAllListagemFuncionariosTercUseCase.items &&
          getAllListagemFuncionariosTercUseCase.items.length > 0
            ? getAllListagemFuncionariosTercUseCase.items[0]
            : null;

        if (funcioanrioTerc) {
          setFuncionario(getAllListagemFuncionariosTercUseCase.items[0]);
          hookForm.reset({
            IdFuncionarioTerc: funcioanrioTerc.IdFuncionarioTerc ?? undefined,
            Ativo: funcioanrioTerc.Ativo === true ? 1 : 0,
            Nome: funcioanrioTerc.Nome,
            IdCentroCusto: funcioanrioTerc.IdCentroCusto,
            IdCargo: funcioanrioTerc.IdCargo,
            IdFornecedor: funcioanrioTerc.IdFornecedor,
            DataInicio: funcioanrioTerc.DataInicio
              ? new Date(funcioanrioTerc.DataInicio)
              : null,
            DataTermino: funcioanrioTerc.DataTermino
              ? new Date(funcioanrioTerc.DataTermino)
              : null,
            StatusAso: funcioanrioTerc.StatusAso,
            Salario: funcioanrioTerc.Salario,
            Cpf: funcioanrioTerc.Cpf,
            Ctps: funcioanrioTerc.Ctps,
            Serie: funcioanrioTerc.Serie,
            CtpsUf: funcioanrioTerc.CtpsUf,
            DataEmissao: funcioanrioTerc.DataEmissao
              ? new Date(funcioanrioTerc.DataEmissao)
              : null,
            RgUf: funcioanrioTerc.RgUf,
            DataExpedicao: funcioanrioTerc.DataExpedicao
              ? new Date(funcioanrioTerc.DataExpedicao)
              : null,
            OrgExp: funcioanrioTerc.OrgExp,
            Imagem: funcioanrioTerc.Imagem,
            ExtensaoFoto: funcioanrioTerc.ExtensaoFoto,
            Observacoes: funcioanrioTerc.Observacoes,
           DataNascimento: funcioanrioTerc.DataNascimento ? new Date(funcioanrioTerc.DataNascimento) : null,
          });

          hookFormEndereco.reset({
            Apartamento: funcioanrioTerc.Apartamento,
            Bairro: funcioanrioTerc.Bairro,
            Cep: funcioanrioTerc.Cep,
            Cidade: funcioanrioTerc.Cidade,
            Rua: funcioanrioTerc.Rua,
            IdEndereco: funcioanrioTerc.IdEndereco ?? undefined,
            Pais: funcioanrioTerc.Pais,
            Numero: funcioanrioTerc.Numero,
            Uf: funcioanrioTerc.Uf,
          });
        }
      } catch (e: unknown) {
        throw new Error("Falha ao buscar Funcionario Tercerizado!");
      }
    },
    [
      filters.IdFuncionarioTerc,
      getAllListagemFuncionariosTercUseCase,
      hookForm,
      hookFormEndereco,
    ],
  );

  const loadCentroCustoSecundarios = useCallback(
    async (params?: { id?: number; page?: number; rowsPerPage?: number }) => {
      try {
        const id = params?.id ?? idFuncionario;
        if (id) {
          await getCentroCustoSecundariosUseCase.getCentroCustoSecundarios(
            id,
            1,
            200,
          );
          setCentroCustoSecundarios(getCentroCustoSecundariosUseCase.items);
        }
      } catch (e: unknown) {
        throw e;
      }
    },
    [idFuncionario, getCentroCustoSecundariosUseCase],
  );

  const loadDocumentos = useCallback(
    async (funcState?: LoadDocumentosState) => {
      try {
        const id = funcState?.id ?? idFuncionario;
        const situacao = funcState?.situacao;
        const dataAte = funcState?.DataAte;
        const dataDe = funcState?.DataDe;
        const status = funcState?.status;
        const NomeDocumento = funcState?.NomeDocumento;

        if (id) {
          await getDocumentosUseCase.getDocumentos(
            id,
            1,
            200,
            status,
            situacao,
            dataDe,
            dataAte,
            NomeDocumento,
          );
          setDocumentos(getDocumentosUseCase.items);
        }
      } catch (e: unknown) {
        setDocumentos([]);
        throw e;
      }
    },
    [getDocumentosUseCase, idFuncionario],
  );

  usePageStartBootstrap(async () => {
    try {
      const jobs = [];
      if (id) {
        setIdFuncionario(Number(id));
        jobs.push([]);
        jobs.push(loadFuncionarioTerc({ IdFuncionarioTerc: Number(id) }));
        jobs.push(loadCentroCustoSecundarios({ id: Number(id) }));
        jobs.push(loadDocumentos({ id: Number(id) }));
        jobs.push(loadFuncionarioTreinamentos({ id: Number(id) }));
      }

      await loadCentroCustoDropDown();
      await loadTreinamentosDropDown();
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  const handleReloadPage = useCallback(async () => {
    try {
      setLoading(true);
      await loadCentroCustoDropDown();
      await loadFuncionarioTerc({ IdFuncionarioTerc: idFuncionario });
      await loadCentroCustoSecundarios({ id: idFuncionario });
      await loadFuncionarioTreinamentos({ id: idFuncionario });
      await loadDocumentos({ id: idFuncionario });
      await loadTreinamentosDropDown();
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao iniciar pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [
    idFuncionario,
    loadCentroCustoDropDown,
    loadCentroCustoSecundarios,
    loadDocumentos,
    loadFuncionarioTerc,
    loadFuncionarioTreinamentos,
    loadTreinamentosDropDown,
    pushErrorNotification,
  ]);
  //#endregion

  //#region  Eventos

  const handleBack = useCallback(() => {
    if (!saving) backOrNavigate("/FuncionarioTercerizacao_lista");
  }, [saving, backOrNavigate]);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      setCurrentTab(newValue);
    },
    [],
  );

  const handleLoadCep = useCallback(
    async (cep: string) => {
      try {
        setErrorMessage(undefined);
        const cepResponse = await getCepUseCase.getCep(cep);
        return cepResponse;
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao carregar CEP!"),
        );
      }
    },
    [getCepUseCase, pushErrorNotification],
  );

  //#region Imagem
  const handleStartFileUpload = useCallback(
    (file: File | null) => {
      if (fileUploadUrl) {
        URL.revokeObjectURL(fileUploadUrl);
      }
      setFileUpload(file);
      setFileUploadURL(file ? URL.createObjectURL(file) : null);
    },
    [fileUploadUrl],
  );

  const handleDeleteFileUpload = useCallback(() => {
    try {
      const imageInput = document.getElementById("profileImageInput");
      if (imageInput instanceof HTMLInputElement) {
        imageInput.value = "";
      }
    } catch (_: unknown) {}
    if (fileUploadUrl) {
      URL.revokeObjectURL(fileUploadUrl);
    }
    if (funcionario && funcionario.Imagem) {
      setFuncionario({ ...funcionario, Imagem: null });
    }
    setFileUpload(null);
    setFileUploadURL(null);
  }, [fileUploadUrl, funcionario]);
  //#endregion

  //#region Funções de salvar
  const handleSubmitFuncionario = useCallback(
    async (form: FuncionarioTercForm) => {
      try {
        if (!saving && !loading) {
          setPageSaving(true);
          setSaving(true);
          let ativio = form.Ativo === 1 ? true : false;
          const enderecoForm = hookFormEndereco.getValues();
          const arquivo1Base64 = fileUpload
            ? await uploadFileToBase64String(fileUpload)
            : funcionario?.Imagem || null;

          if (!idFuncionario) {
            const newIdFuncionario =
              await createFuncionariosTerUseCase.createFuncionariosTerc(
                ativio ?? null,
                form.Nome,
                form.IdCentroCusto ?? null,
                form.IdCargo ?? null,
                form.IdFornecedor ?? null,
                form.DataInicio ?? null,
                form.DataTermino ?? null,
                form.StatusAso ?? null,
                form.Salario ?? null,
                form.Cpf ?? null,
                form.Ctps ?? null,
                form.Serie ?? null,
                form.CtpsUf ?? null,
                form.DataEmissao ?? null,
                form.RgUf ?? null,
                form.DataExpedicao ?? null,
                form.OrgExp ?? null,
                arquivo1Base64 ?? null,
                form.ExtensaoFoto ?? null,
                form.Observacoes ?? null,

                //Endereco
                enderecoForm.Rua ?? null,
                enderecoForm.Numero ?? null,
                enderecoForm.Apartamento ?? null,
                enderecoForm.Cep ?? null,
                enderecoForm.Uf ?? null,
                enderecoForm.Cidade ?? null,
                enderecoForm.Bairro ?? null,
                enderecoForm.Pais ?? null,
                form.DataNascimento ?? null
              );

            setIdFuncionario(newIdFuncionario);

            setLoading(true);

            // await loadFuncionario({ idFuncionario: newIdFuncionario });
            // await loadPage(newIdFuncionario, false);

            const currentPath = window.location.pathname.replace(/\/$/, "");
            const newUrl = `${currentPath}/${newIdFuncionario}`;
            window.history.pushState({}, "", newUrl);

            setLoading(false);
            pushInfoNotification("Sucesso", "Criado com sucesso!", {
              isAutoFinish: true,
            });
          } else {
            await updateFuncionariosTercUseCase.updateFuncionariosTerc(
              idFuncionario,
              ativio ?? null,
              form.Nome,
              form.IdCentroCusto ?? null,
              form.IdCargo ?? null,
              form.IdFornecedor ?? null,
              form.DataInicio ?? null,
              form.DataTermino ?? null,
              form.StatusAso ?? null,
              form.Salario ?? null,
              form.Cpf ?? null,
              form.Ctps ?? null,
              form.Serie ?? null,
              form.CtpsUf ?? null,
              form.DataEmissao ?? null,
              form.RgUf ?? null,
              form.DataExpedicao ?? null,
              form.OrgExp ?? null,
              arquivo1Base64 ?? null,
              form.ExtensaoFoto ?? null,
              form.Observacoes ?? null,
              //Endereco
              enderecoForm.Rua ?? null,
              enderecoForm.Numero ?? null,
              enderecoForm.Apartamento ?? null,
              enderecoForm.Cep ?? null,
              enderecoForm.Uf ?? null,
              enderecoForm.Cidade ?? null,
              enderecoForm.Bairro ?? null,
              enderecoForm.Pais ?? null,
              form.DataNascimento ?? null
            );
            pushInfoNotification("Sucesso", "Atualizado com sucesso!", {
              isAutoFinish: true,
            });
          }
        }
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao salvar usuário!"),
        );
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    },
    [
      saving,
      loading,
      setPageSaving,
      hookFormEndereco,
      fileUpload,
      funcionario?.Imagem,
      idFuncionario,
      createFuncionariosTerUseCase,
      pushInfoNotification,
      updateFuncionariosTercUseCase,
      pushErrorNotification,
    ],
  );

  const handleCadastroCentroCustoSecundarioOpenState = useCallback(
    (idCentroCustoSecundario: number | null, openState: boolean) => {
      if (openState) {
        if (idCentroCustoSecundario && centroCustoSecundarios) {
          const item = centroCustoSecundarios.find(
            (x) => x.idCentroCustoSecundario === idCentroCustoSecundario,
          );
          setSelectedCentroCustoSecundario(item);
        } else {
          setSelectedCentroCustoSecundario(undefined);
        }
      }
      setOpenCadastroCentroCustoSecundarioModal(openState);
    },
    [centroCustoSecundarios],
  );

  const handleDeletarCentroCustoSecundarioOpenState = useCallback(
    (idCentroCustoSecundario: number | null, openState: boolean) => {
      if (openState) {
        if (idCentroCustoSecundario && centroCustoSecundarios) {
          setSelectedCentroCustoSecundario(
            centroCustoSecundarios.find(
              (x) => x.idCentroCustoSecundario === idCentroCustoSecundario,
            ),
          );
        } else {
          setSelectedCentroCustoSecundario(undefined);
        }
      }
      setOpenDeletarCentroCustoSecundarioModal(openState);
    },
    [centroCustoSecundarios],
  );

  const handleSubmitCentroCustoSecundario = useCallback(
    async (form: CentroCustoSecundarioForm) => {
      try {
        if (!saving && !loading) {
          setSaving(true);
          if (idFuncionario) {
            if (!selectedCentroCustoSecundario) {
              await createCentroCustoSecundarioUseCase.createCentroCustoSecundario(
                idFuncionario,
                form.idCentroCusto,
              );
            } else {
              await updateCentroCustoSecundarioUseCase.updateCentroCustoSecundario(
                selectedCentroCustoSecundario.idCentroCustoSecundario,
                form.idCentroCusto,
              );
            }
            await loadCentroCustoSecundarios();
          }
        }
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao salvar centro de custo secundario!"),
        );
      } finally {
        setSaving(false);
        setOpenCadastroCentroCustoSecundarioModal(false);
      }
    },
    [
      saving,
      loading,
      idFuncionario,
      selectedCentroCustoSecundario,
      loadCentroCustoSecundarios,
      createCentroCustoSecundarioUseCase,
      updateCentroCustoSecundarioUseCase,
      pushErrorNotification,
    ],
  );

  const handleConfirmarDeletarCentroCustoSecundario = useCallback(async () => {
    try {
      if (selectedCentroCustoSecundario) {
        setSaving(true);
        await deleteCentroCustoSecundarioUseCase.deleteCentroCustoSecundario(
          selectedCentroCustoSecundario.idCentroCustoSecundario,
        );
        await loadCentroCustoSecundarios();
      }
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao deletar centro de custo!"),
      );
    } finally {
      setSaving(false);
      setSelectedCentroCustoSecundario(undefined);
      setOpenDeletarCentroCustoSecundarioModal(false);
    }
  }, [
    selectedCentroCustoSecundario,
    deleteCentroCustoSecundarioUseCase,
    loadCentroCustoSecundarios,
    pushErrorNotification,
  ]);

  //#region Documentos
  const handleCadastroDocumentoOpenState = useCallback(
    (idDocumento: number | null, openState: boolean) => {
      if (openState) {
        if (documentos && idDocumento) {
          const documento = documentos.find(
            (x) => x.idDocumento === idDocumento,
          );
          setSelectedDocumento(documento);
        } else {
          setSelectedDocumento(undefined);
        }
      }
      setOpenCadastroDocumentoModal(openState);
    },
    [documentos],
  );

  const handleDocumentoOpenState = useCallback(
    (idDocumento: number | null, openState: boolean) => {
      setOpenDocumentoModal(openState);
    },
    [],
  );

  const handleDeletarDocumentoOpenState = useCallback(
    (idDocumento: number | null, openState: boolean) => {
      if (openState) {
        if (idDocumento && documentos) {
          setSelectedDocumento(
            documentos.find((x) => x.idDocumento === idDocumento),
          );
        } else {
          setSelectedDocumento(undefined);
        }
      }
      setOpenDeletarDocumentoModal(openState);
    },
    [documentos],
  );

  const handleSubmitDocumento = useCallback(
    async (form: DocumentoForm) => {
      try {
        if (!saving && !loading) {
          setSaving(true);
          if (idFuncionario) {
            if (selectedDocumento) {
              await updateDocumentoUseCase.updateDocumento(
                selectedDocumento.idDocumento,
                selectedDocumento.nome,
                form.statusArquivo,
                form.nomeDocumento,
                form.dataVencimento,
                form.file,
                form.limparArquivos,
              );
            } else {
              await createDocumentoUseCase.createDocumento(
                Number(idFuncionario),
                form.statusArquivo,
                form.nomeDocumento,
                form.dataVencimento,
                form.file,
              );
            }
            await loadDocumentos();
          }
        }
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao salvar documento!"),
        );
      } finally {
        setSaving(false);
        setOpenCadastroDocumentoModal(false);
      }
    },
    [
      saving,
      loading,
      idFuncionario,
      selectedDocumento,
      loadDocumentos,
      updateDocumentoUseCase,
      createDocumentoUseCase,
      pushErrorNotification,
    ],
  );

  const handleConfirmarDeletarDocumento = useCallback(async () => {
    try {
      if (selectedDocumento) {
        setSaving(true);
        await deleteDocumentoUseCase.deleteDocumento(
          selectedDocumento.idDocumento,
        );

        await loadDocumentos();
      }
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao deletar documento!"),
      );
    } finally {
      setSaving(false);
      setSelectedDocumento(undefined);
      setOpenDeletarDocumentoModal(false);
    }
  }, [
    selectedDocumento,
    deleteDocumentoUseCase,
    loadDocumentos,
    pushErrorNotification,
  ]);

  const handleDownloadDocumento = useCallback(
    async (idDocumento: number, imageName: string) => {
      try {
        setCustomLoading(true);
        await downloadFuncionarioDocumentoByIdUseCase.downloadFuncionarioDocumentoById(
          idDocumento,
          imageName,
        );
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao baixar documento!"),
        );
      } finally {
        setCustomLoading(false);
      }
    },
    [
      downloadFuncionarioDocumentoByIdUseCase,
      pushErrorNotification,
      setCustomLoading,
    ],
  );

  //#endregion

  //#region treinamentos
  const handleCadastroFuncionarioTreinamentoOpenState = useCallback(
    (idFuncionarioTreinamento: number | null, openState: boolean) => {
      if (openState) {
        if (funcionarioTreinamentos && idFuncionarioTreinamento) {
          const treinamento = funcionarioTreinamentos.find(
            (x) => x.idFuncionarioTreinamento === idFuncionarioTreinamento,
          );
          setSelectedFuncionarioTreinamento(treinamento);
        } else {
          setSelectedFuncionarioTreinamento(undefined);
        }
      }
      setOpenCadastroFuncionarioTreinamentoModal(openState);
    },
    [funcionarioTreinamentos],
  );

  const handleAvaliarFuncionarioTreinamentoOpenState = useCallback(
    (idFuncionarioTreinamento: number | null, openState: boolean) => {
      if (openState) {
        if (funcionarioTreinamentos && idFuncionarioTreinamento) {
          const treinamento = funcionarioTreinamentos.find(
            (x) => x.idFuncionarioTreinamento === idFuncionarioTreinamento,
          );
          setSelectedFuncionarioTreinamento(treinamento);
          setSelectedIdFuncionarioTreinamentosLote(undefined);
        } else {
          setSelectedFuncionarioTreinamento(undefined);
        }
      }
      setOpenAvaliarFuncionarioTreinamentoModal(openState);
    },
    [funcionarioTreinamentos],
  );

  const handleAvaliarLoteTreinamentoOpenState = useCallback(
    (idFuncionarioTreinamentoLote: number[], openState: boolean) => {
      if (idFuncionarioTreinamentoLote) {
        setSelectedIdFuncionarioTreinamentosLote(idFuncionarioTreinamentoLote);
        setSelectedFuncionarioTreinamento(undefined);
      } else {
        setSelectedIdFuncionarioTreinamentosLote(undefined);
      }
      setOpenAvaliarFuncionarioTreinamentoModal(openState);
    },
    [],
  );

  const handleEditarFuncionarioTreinamento = useCallback(
    (idFuncionarioTreinamento: number | null, openState: boolean) => {
      if (openState) {
        if (funcionarioTreinamentos && idFuncionarioTreinamento) {
          const treinamento = funcionarioTreinamentos.find(
            (x) => x.idFuncionarioTreinamento === idFuncionarioTreinamento,
          );
          setSelectedFuncionarioTreinamento(treinamento);
        } else {
          setSelectedFuncionarioTreinamento(undefined);
        }
      }
      setOpenCadastroFuncionarioTreinamentoModal(openState);
    },
    [funcionarioTreinamentos],
  );

  const handleDeletarFuncionarioTreinamentoOpenState = useCallback(
    (idFuncionarioTreinamento: number | null, openState: boolean) => {
      if (openState) {
        if (idFuncionarioTreinamento && funcionarioTreinamentos) {
          setSelectedFuncionarioTreinamento(
            funcionarioTreinamentos.find(
              (x) => x.idFuncionarioTreinamento === idFuncionarioTreinamento,
            ),
          );
        } else {
          setSelectedFuncionarioTreinamento(undefined);
        }
      }
      setOpenDeletarFuncionarioTreinamentoModal(openState);
    },
    [funcionarioTreinamentos],
  );

  const handleDeletarTreinamentosLoteOpenState = useCallback(
    (idFuncionarioTreinamentosLote: number[], openState: boolean) => {
      setSelectedIdFuncionarioTreinamentosLote(idFuncionarioTreinamentosLote);
      setOpenDeletarFuncionarioTreinamentosLoteModal(openState);
    },
    [],
  );

  const handleSubmitTreinamento = useCallback(
    async (form: TreinamentoForm) => {
      try {
        if (!saving && !loading) {
          setSaving(true);

          if (idFuncionario) {
            if (!selectedFuncionarioTreinamento) {
              await createFuncionarioTreinamentoUseCase.createFuncionarioTreinamento(
                Number(idFuncionario),
                form.idTreinamento,
                form.cargaHoraria,
                form.local,
                form.instrutor,
                form.dataRealização,
                form.prazoAvaliacao,
              );
            } else {
              await updateFuncionarioTreinamentoUseCase.updateFuncionarioTreinamento(
                selectedFuncionarioTreinamento.idFuncionarioTreinamento,
                form.idTreinamento,
                form.cargaHoraria,
                form.local,
                form.instrutor,
                form.dataRealização,
                form.prazoAvaliacao,
              );
            }
            await loadFuncionarioTreinamentos();
          }
        }
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao salvar documento!"),
        );
      } finally {
        setSaving(false);
        setOpenCadastroFuncionarioTreinamentoModal(false);
      }
    },
    [
      saving,
      loading,
      idFuncionario,
      selectedFuncionarioTreinamento,
      loadFuncionarioTreinamentos,
      createFuncionarioTreinamentoUseCase,
      updateFuncionarioTreinamentoUseCase,
      pushErrorNotification,
    ],
  );

  const handleConfirmarDeletarFuncionarioTreinamento = useCallback(async () => {
    if (selectedFuncionarioTreinamento) {
      try {
        setSaving(true);
        await deleteFuncionarioTreinamentoUseCase.deleteFuncionarioTreinamento(
          selectedFuncionarioTreinamento.idFuncionarioTreinamento,
        );

        await loadFuncionarioTreinamentos();
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao deletar funcionario treinamento!"),
        );
      } finally {
        setSaving(false);
        setSelectedFuncionarioTreinamento(undefined);
        setOpenDeletarFuncionarioTreinamentoModal(false);
      }
    }
  }, [
    deleteFuncionarioTreinamentoUseCase,
    loadFuncionarioTreinamentos,
    pushErrorNotification,
    selectedFuncionarioTreinamento,
  ]);

  const handleSubmitAvaliarTreinamento = useCallback(
    async (form: AvaliarTreinamentoForm) => {
      try {
        if (!saving && !loading) {
          setSaving(true);

          if (idFuncionario) {
            if (selectedFuncionarioTreinamento) {
              await avaliarTreinamentoUseCase.avaliarTreinamento(
                selectedFuncionarioTreinamento.idFuncionarioTreinamento,
                form.eficaz,
                form.avaliador,
                form.dataAvaliacao,
                form.evidenciaObjetiva,
              );
            } else if (selectedIdFuncionarioTreinamentosLote) {
              await avaliarTreinamentoUseCase.avaliarTreinamentoLote(
                selectedIdFuncionarioTreinamentosLote,
                form.eficaz,
                form.avaliador,
                form.dataAvaliacao,
                form.evidenciaObjetiva,
              );
            }
            await loadFuncionarioTreinamentos();
          }
        }
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao salvar documento!"),
        );
      } finally {
        setSaving(false);
        setOpenAvaliarFuncionarioTreinamentoModal(false);
      }
    },
    [
      saving,
      loading,
      idFuncionario,
      selectedFuncionarioTreinamento,
      selectedIdFuncionarioTreinamentosLote,
      loadFuncionarioTreinamentos,
      avaliarTreinamentoUseCase,
      pushErrorNotification,
    ],
  );

  //#endregion

  //#endregion
  //#endregion

  //#region  relatorio
  const handleReportFuncionarioTreinamento = useCallback(
    async (idFuncionarioTreinamento: number) => {
      try {
        setLoading(true);

        await startReportViewerProcess({
          fetchCallback: async (requiredMimeType) =>
            await getReportFuncionarioTreinamentoTercUseCase.getReportFuncionarioTreinamentoTerc(
              requiredMimeType,
              idFuncionarioTreinamento,
            ),
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(
            e,
            "Falha ao gerar relatorio de Funcionario Treinamento!",
          ),
        );
      } finally {
        setLoading(false);
      }
    },
    [
      getReportFuncionarioTreinamentoTercUseCase,
      pushErrorNotification,
      startReportViewerProcess,
    ],
  );

  //#endregion
  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } else {
        if (idFuncionario) {
          changeHistory.handleOpenChangeHistory(
            idFuncionario,
            LogTipoTabela.FUNCIONARIO_TERC,
            true,
          );
        }
      }
    },
    [idFuncionario, changeHistory],
  );

  //#region  Filtros
  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      filtersDoc.clearFilters();
      // pagination.setPage(1);
      await loadDocumentos({
        DataAte: null,
        DataDe: null,
        situacao: 2,
        status: 0,
        NomeDocumento: null,
        page: 1,
      });
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar Documentos!"),
      );
    } finally {
      setLoading(false);
    }
  }, [filtersDoc, loadDocumentos, pushErrorNotification]);

  const handleStatusChange = useDebouncedCallback(
    debouncedRef,
    (newValue: number) => {
      useDocumentoFilters.getState().setStatus(newValue);
      return async () => {
        try {
          setLoading(true);
          // useDiarioObrasPagination.getState().setPage(1);
          await loadDocumentos({ status: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao carregar Documentos!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadDocumentos, pushErrorNotification],
  );

  const handleSituacaoChange = useDebouncedCallback(
    debouncedRef,
    (newValue: number) => {
      useDocumentoFilters.getState().setSituacao(newValue);
      return async () => {
        try {
          setLoading(true);
          // useDiarioObrasPagination.getState().setPage(1);
          await loadDocumentos({ situacao: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao carregar Documentos!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadDocumentos, pushErrorNotification],
  );

  const handleDataRangeChange = useDebouncedCallback(
    debouncedRef,
    (de: Date | null, ate: Date | null) => {
      useDocumentoFilters.setState({ DataDe: de, DataAte: ate });
      return async () => {
        try {
          setLoading(true);
          await loadDocumentos({ DataDe: de, DataAte: ate, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar dados!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadDocumentos, pushErrorNotification],
  );

  const handleNomeDocumentoChange = useDebouncedCallback(
    debouncedRef,
    (newValue: string | null) => {
      useDocumentoFilters.getState().setNomeDocumento(newValue);
      return async () => {
        try {
          setLoading(true);
          // useDocumentoPagination.getState().setPage(1);
          await loadDocumentos({ NomeDocumento: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao carregar Documentos!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadDocumentos, pushErrorNotification],
  );

  //#endregion
  return {
    idFuncionario,
    isCreated,
    pagination,
    filters,
    errorMessage,
    currentTab,
    saving,
    funcionario,
    loading,
    hookForm,
    hookFormEndereco,
    fileUploadUrl,
    centroCustoSecundarios,
    openCadastroCentroCustoSecundarioModal,
    selectedCentroCustoSecundario,
    openDeletarCentroCustoSecundarioModal,
    openDeletarDocumentoModal,
    openCadastroDocumentoModal,
    selectedDocumento,
    documentos,
    funcionarioTreinamentos: funcionarioTreinamentos ?? [],
    selectedFuncionarioTreinamento,
    openCadastroFuncionarioTreinamentoModal,
    openDeletarFuncionarioTreinamentoModal,
    openAvaliarFuncionarioTreinamentoModal,
    reportViewerState,
    changeHistory,
    openDocumentoModal,
    filtersDoc,
    handleSubmit: hookForm.handleSubmit(handleSubmitFuncionario),
    handleReloadPage,
    handleBack,
    handleTabChange,
    handleLoadCep,
    handleDeleteFileUpload,
    handleStartFileUpload,
    handleCadastroCentroCustoSecundarioOpenState,
    handleDeletarCentroCustoSecundarioOpenState,
    handleSubmitCentroCustoSecundario,
    handleConfirmarDeletarCentroCustoSecundario,
    handleConfirmarDeletarDocumento,
    handleSubmitDocumento,
    handleDeletarDocumentoOpenState,
    handleCadastroDocumentoOpenState,
    handleOpenChangeHistory,
    handleDownloadDocumento,
    handleCadastroFuncionarioTreinamentoOpenState,
    handleAvaliarFuncionarioTreinamentoOpenState,
    handleAvaliarLoteTreinamentoOpenState,
    handleEditarFuncionarioTreinamento,
    handleDeletarTreinamentosLoteOpenState,
    handleDeletarFuncionarioTreinamentoOpenState,
    handleSubmitTreinamento,
    handleConfirmarDeletarFuncionarioTreinamento,
    handleSubmitAvaliarTreinamento,
    handleReportFuncionarioTreinamento,
    handleDocumentoOpenState,
    handleDataRangeChange,
    handleSituacaoChange,
    handleStatusChange,
    handleClearFilters,
    handleNomeDocumentoChange,
  };
};
